import React from 'react';
import { RefreshCw, Video, Globe, MousePointer, Users } from 'lucide-react';

export const POOL_TYPES = [
    { value: 'engagement', label: 'Ad Engagers', icon: MousePointer, description: 'Users who engaged with your ads' },
    { value: 'video_viewer', label: 'Video Viewers', icon: Video, description: 'Users who watched your videos' },
    { value: 'website', label: 'Website Visitors', icon: Globe, description: 'Users who visited your website' },
    { value: 'custom', label: 'Custom Pool', icon: Users, description: 'Define custom criteria' }
];

export const TRIGGER_TYPES = [
    { value: 'engagement', label: 'Any Engagement' },
    { value: 'video_view', label: 'Video View' },
    { value: 'click', label: 'Click-through' },
    { value: 'website_visit', label: 'Website Visit' },
    { value: 'conversion', label: 'Conversion Event' }
];

export const FUNNEL_STAGES = [
    { value: 'TOFU', label: 'Awareness (TOFU)', color: 'var(--color-info)' },
    { value: 'MOFU', label: 'Consideration (MOFU)', color: 'var(--color-warning)' },
    { value: 'BOFU', label: 'Conversion (BOFU)', color: 'var(--color-success)' }
];

export const LOOKBACK_OPTIONS = [7, 14, 30, 60, 90, 180];

/**
 * RetargetingPoolForm - Reusable form component for RetargetingPool node editing
 * Used in both RetargetingPoolModal and PropertiesPanel
 */
const RetargetingPoolForm = ({
    formData,
    onChange,
    errors = {}
}) => {
    const handleChange = (field, value) => {
        onChange(field, value);
    };

    const handleTriggerChange = (field, value) => {
        onChange('trigger', { ...(formData.trigger || { type: 'engagement', threshold: 1, lookbackDays: 30 }), [field]: value });
    };

    const trigger = formData.trigger || { type: 'engagement', threshold: 1, lookbackDays: 30 };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Pool Name */}
            <div className="input-group">
                <label className="input-label">Pool Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., TOFU Engagers, Video Completers"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Pool Type Selection */}
            <div className="input-group">
                <label className="input-label">Pool Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                    {POOL_TYPES.map(type => {
                        const Icon = type.icon;
                        const isSelected = formData.poolType === type.value;
                        return (
                            <button
                                key={type.value}
                                type="button"
                                onClick={() => handleChange('poolType', type.value)}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    background: isSelected ? 'var(--color-warning-light, rgba(245, 158, 11, 0.1))' : 'var(--color-bg-secondary)',
                                    border: isSelected ? '2px solid var(--color-warning)' : '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    textAlign: 'center'
                                }}
                            >
                                <Icon size={24} style={{ color: isSelected ? 'var(--color-warning)' : 'var(--color-text-tertiary)' }} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                        {type.label}
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                        {type.description}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Source & Target Stage */}
            <div style={{
                padding: 'var(--space-4)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-lg)'
            }}>
                <h3 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)' }}>Audience Flow</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div style={{ flex: 1 }}>
                        <label className="input-label">Source Stage</label>
                        <select
                            className="select-field"
                            value={formData.sourceStage || 'TOFU'}
                            onChange={(e) => handleChange('sourceStage', e.target.value)}
                        >
                            {FUNNEL_STAGES.map(stage => (
                                <option key={stage.value} value={stage.value}>{stage.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: 'var(--space-4)'
                    }}>
                        <RefreshCw size={20} style={{ color: 'var(--color-warning)' }} />
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>→</span>
                    </div>
                    <div style={{ flex: 1 }}>
                        <label className="input-label">Target Stage</label>
                        <select
                            className="select-field"
                            value={formData.targetStage || 'MOFU'}
                            onChange={(e) => handleChange('targetStage', e.target.value)}
                            style={errors.targetStage ? { borderColor: 'var(--color-error)' } : {}}
                        >
                            {FUNNEL_STAGES.map(stage => (
                                <option key={stage.value} value={stage.value}>{stage.label}</option>
                            ))}
                        </select>
                        {errors.targetStage && (
                            <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                                {errors.targetStage}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Trigger Configuration */}
            <div className="input-group">
                <label className="input-label">Trigger Configuration</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
                    <div>
                        <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)', display: 'block' }}>
                            Trigger Type
                        </label>
                        <select
                            className="select-field"
                            value={trigger.type}
                            onChange={(e) => handleTriggerChange('type', e.target.value)}
                        >
                            {TRIGGER_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)', display: 'block' }}>
                            Threshold
                        </label>
                        <input
                            type="number"
                            className="input-field"
                            min="1"
                            value={trigger.threshold}
                            onChange={(e) => handleTriggerChange('threshold', parseInt(e.target.value) || 1)}
                        />
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            {trigger.type === 'video_view' ? '% watched' : 'times'}
                        </span>
                    </div>
                    <div>
                        <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)', display: 'block' }}>
                            Lookback Window
                        </label>
                        <select
                            className="select-field"
                            value={trigger.lookbackDays}
                            onChange={(e) => handleTriggerChange('lookbackDays', parseInt(e.target.value))}
                        >
                            {LOOKBACK_OPTIONS.map(days => (
                                <option key={days} value={days}>{days} days</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Active Toggle */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)'
            }}>
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Pool Status</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                        {formData.isActive !== false ? 'Pool is actively collecting users' : 'Pool is paused'}
                    </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 'var(--space-2)' }}>
                    <span style={{
                        color: formData.isActive !== false ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        {formData.isActive !== false ? 'Active' : 'Paused'}
                    </span>
                    <input
                        type="checkbox"
                        checked={formData.isActive !== false}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        style={{ width: '20px', height: '20px' }}
                    />
                </label>
            </div>
        </div>
    );
};

export default RetargetingPoolForm;
